import {
  customElement,
  DeesElement,
  html,
  css,
  cssManager,
  property,
  type TemplateResult,
} from '@design.estate/dees-element';
import { demoFunc } from './dees-panel.demo.js';
import { themeDefaultStyles } from '../../00theme.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-panel': DeesPanel;
  }
}

@customElement('dees-panel')
export class DeesPanel extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Layout'];

  @property({ type: String })
  accessor title: string = '';

  @property({ type: String })
  accessor subtitle: string = '';

  @property({ type: String })
  accessor variant: 'default' | 'outline' | 'ghost' = 'default';

  @property({ type: String })
  accessor size: 'sm' | 'md' | 'lg' = 'md';

  @property({ attribute: false })
  accessor runAfterRender: ((elementArg: HTMLElement) => void | Promise<void>) | undefined = undefined;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      /* TODO: Migrate hardcoded values to --dees-* CSS variables */
      :host {
        display: block;
        font-family: var(--dees-font-family);
        background: var(--dees-color-bg-primary);
        border-radius: var(--dees-radius-xl);
        corner-shape: var(--dees-corner-shape);
        padding: var(--dees-spacing-xl);
        border: 1px solid var(--dees-color-border-default);
        transition: all var(--dees-transition-default) var(--dees-ease-standard);
      }

      /* Variant: default */
      :host([variant="default"]) {
        box-shadow: var(--dees-shadow-xs);
      }

      /* Variant: outline */
      :host([variant="outline"]) {
        background: transparent;
        box-shadow: none;
      }

      /* Variant: ghost */
      :host([variant="ghost"]) {
        background: transparent;
        border-color: transparent;
        box-shadow: none;
        padding: var(--dees-spacing-lg);
      }

      /* Size variations */
      :host([size="sm"]) {
        padding: var(--dees-spacing-lg);
      }

      :host([size="lg"]) {
        padding: var(--dees-spacing-2xl);
      }

      .header {
        margin-bottom: var(--dees-spacing-lg);
      }

      .header:empty {
        display: none;
      }

      .title {
        margin: 0;
        font-size: var(--dees-font-title3-size);
        font-weight: 600;
        color: var(--dees-color-text-primary);
        letter-spacing: -0.025em;
        line-height: 1.5;
      }

      /* Title size variations */
      :host([size="sm"]) .title {
        font-size: var(--dees-font-headline-size);
      }

      :host([size="lg"]) .title {
        font-size: var(--dees-font-title2-size);
      }

      .subtitle {
        margin: var(--dees-spacing-xs) 0 0 0;
        font-size: var(--dees-font-control-size);
        color: var(--dees-color-text-muted);
        letter-spacing: -0.006em;
        line-height: 1.5;
      }

      /* Subtitle size variations */
      :host([size="sm"]) .subtitle {
        font-size: var(--dees-font-control-size-sm);
      }

      :host([size="lg"]) .subtitle {
        font-size: var(--dees-font-subhead-size);
        margin-top: 6px;
      }

      .content {
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-control-size);
        line-height: 1.6;
      }

      /* Content size variations */
      :host([size="sm"]) .content {
        font-size: var(--dees-font-control-size-sm);
      }

      :host([size="lg"]) .content {
        font-size: var(--dees-font-subhead-size);
      }

      /* Remove margins from first and last children */
      .content ::slotted(*:first-child) {
        margin-top: 0;
      }

      .content ::slotted(*:last-child) {
        margin-bottom: 0;
      }

      /* Nested panels spacing */
      ::slotted(dees-panel) {
        margin-top: var(--dees-spacing-lg);
      }

      ::slotted(dees-panel:first-child) {
        margin-top: 0;
      }
    `,
  ];

  public render(): TemplateResult {
    return html`
      <div class="header">
        ${this.title ? html`<h3 class="title">${this.title}</h3>` : ''}
        ${this.subtitle ? html`<p class="subtitle">${this.subtitle}</p>` : ''}
      </div>
      <div class="content">
        <slot></slot>
      </div>
    `;
  }

  public async firstUpdated() {
    if (this.runAfterRender) {
      await this.runAfterRender(this);
    }
  }
}